import { isEmpty } from '@jfs/utils'

import { flow, types } from 'mobx-state-tree'
import { auditForAMidApi } from '../../network/auditForAMidApi'
import { bulkEssProcessApprovals } from '../../network/bulkEssProcessApprovals'
import { downloadRiskTnxsEvidence } from '../../network/downloadRiskTnxsEvidence'
import { downloadExportAuditApi, exportAuditApi } from '../../network/exportAuditApi'
import { fetchGroupByRiskAudit } from '../../network/fetchGroupByRiskAudit'
import { fetchGroupByRiskTnxApi } from '../../network/fetchGroupByRiskTnxApi'
import { fetchSearchEntityReq } from '../../network/fetchSearchEntity'
import { getMidPayoutConfig } from '../../network/getMidPayoutConfig'
import { processTransactionApi } from '../../network/processTransactionApi'
import { riskControlMerchantProcessApproval } from '../../network/riskControlMerchantProcessApproval'
import { riskControlTxnProcessApproval } from '../../network/riskControlTxnProcessApproval'
import { searchTxnsReq } from '../../network/searchTxnsReq'
import { updateMidPayoutConfigReq } from '../../network/updateMidPayoutConfig'
import { updateTxnStatusReq } from '../../network/updateRiskTxnStatus'
import { uploadRiskTnxsEvidenceApi } from '../../network/uploadRiskTnxsEvidenceApi'
import {
  DateFormatTypes,
  MessageTypes,
  dateFormatter,
  dateFormatterForDDMMYYYY,
  delay,
  generateRandomString,
  getErrorMessage,
  getHHMMSS,
  getIncreasingDelay,
  riskHoldStatusMap,
  riskUpdateMap,
  statusActionMap,
} from '../../utils'
import { mimeTypes } from '../../utils/constants'
import { RootStore } from '../store'

const Transaction = types.model('Transaction', {
  id: types.optional(types.string, ''),
  jiopayOrigTransactionId: types.optional(types.string, ''),
  settlementGrossAmount: types.optional(types.string, ''),
  internalId: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  merchantId: types.optional(types.string, ''),
  originalTransactionId: types.optional(types.string, ''),
  invoice: types.optional(types.string, ''),
  transDateTime: types.optional(types.string, ''),
  transactionType: types.optional(types.string, ''),
  isSelected: types.optional(types.boolean, false),
  settlementNetAmount: types.optional(types.string, ''),
  created: types.optional(types.string, ''),
})

const GroupedTransaction = types.model('GroupedTransaction', {
  businessName: types.optional(types.string, ''),
  created: types.optional(types.string, ''),
  merchantId: types.optional(types.string, ''),
  settlementNetAmount: types.optional(types.string, ''),
  totalCount: types.optional(types.number, 0),
})

const Pagination = types.model('Pagination', {
  size: types.optional(types.number, 0),
  totalElements: types.optional(types.number, 0),
  totalPages: types.optional(types.number, 0),
  number: types.optional(types.number, 0),
})

const GroupedAuditItem = types.model('GroupedAuditItem', {
  entityId: types.optional(types.string, ''),
  entityName: types.optional(types.string, ''),
  settlementNetAmount: types.optional(types.string, ''),
  totalCount: types.optional(types.string, ''),
})

const AuditForAMid = types.model('AuditForAMid', {
  id: types.optional(types.number, -1),
  entityId: types.optional(types.string, ''),
  entityName: types.optional(types.string, ''),
  settlementNetAmount: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  createdBy: types.optional(types.string, ''),
  createdAt: types.optional(types.string, ''),
  originalTransactionId: types.optional(types.string, ''),
  comments: types.maybeNull(types.string),
  docId: types.maybeNull(types.optional(types.string, '')),
  actionDownload: types.optional(types.string, 'Download'),
})

const RiskConfig = types
  .model('RiskConfig', {
    groupedRiskTnsx: types.array(GroupedTransaction),
    transactions: types.array(Transaction),
    groupedAudit: types.array(GroupedAuditItem),
    auditForAMid: types.array(AuditForAMid),
    pagination: Pagination,
    riskStatus: types.optional(types.string, ''),
    selectedEntityId: types.optional(types.string, ''),
  })
  .actions((self) => ({
    searchTxns: flow(function* searchTxns(searchBy: string, searchString: string, abortController?: any) {
      console.log('searchTxns abortController', abortController)
      try {
        const pageNumber = RootStore?.pagination?.number === 0 ? 1 : RootStore?.pagination?.number
        const size = RootStore?.pagination?.size < 10 ? 10 : RootStore?.pagination?.size
        const startDate = `${dateFormatter(RootStore?.selectedDateInfo?.fromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          RootStore?.selectedDateInfo?.fromDate,
        )}` //'2023-07-01 00:00:00'
        const endDate = `${dateFormatter(RootStore?.selectedDateInfo?.toDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          RootStore?.selectedDateInfo?.toDate,
        )}`
        const req = {
          inputFilter: {
            startDateTime: startDate,
            endDateTime: endDate,
            ...(searchBy?.length > 0 && searchString?.length > 0 && { [searchBy]: [searchString] }),
            ...(self.riskStatus?.length > 0 &&
              searchBy !== 'Merchant ID' &&
              searchString?.length === 0 && { status: [self.riskStatus] }),
          },
          configParams: {
            mediaType: 'JSON',
            page: pageNumber,
            size: size,
          },
          outputDomains: [
            'originalTransactionId',
            'jiopayOrigTransactionId',
            'merchantId',
            'settlementDate',
            'terminalId',
            'bankReferenceNumber',
            'invoice',
            'transDateTime',
            'paymentInstrument',
            'transactionType',
            'cardNumber',
            'settlementNetAmount',
            'settlementGrossAmount',
            'referenceNumber',
            'status',
            'externalReferenceNumber',
            'payoutSequenceNumber',
            'internalId',
          ], // TODO: Tabrez has to share list of columns
        }
        const response = yield searchTxnsReq(req, RootStore.session.getTokens, abortController)
        self.transactions = JSON.parse(JSON.stringify(response?.content))
        RootStore.setPagination(response?.page)
        if (response?.content?.length === 0) {
          throw { message: 'No Record found' }
        }
        return response
      } catch (error) {
        RootStore.setPagination({
          size: 0,
          totalElements: 0,
          totalPages: 0,
          number: 0,
        })
        self.transactions = [] as any
        if (error && error?.data?.errorCode !== 'ERR_CANCELED') {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    searchRiskTxns: flow(function* searchRiskTxns(
      status: string,
      mid: string,
      date: string,
      pageNumber,
      size,
      abortController?: any,
    ) {
      self.transactions = [] as any
      console.log(date)
      try {
        const startDate = `${dateFormatterForDDMMYYYY(date)} 00:00:00` //'2023-07-01 00:00:00'
        const endDate = `${dateFormatterForDDMMYYYY(date)} 23:59:59`
        const req = {
          inputFilter: {
            startDateTime: startDate,
            endDateTime: endDate,
            merchantId: mid,
            status: [status],
            transactionType: 'TRANSACTION',
            excludeSplit: true,
          },
          configParams: {
            mediaType: 'JSON',
            page: pageNumber,
            size: size,
          },
          outputDomains: [
            'originalTransactionId',
            'jiopayOrigTransactionId',
            'merchantId',
            'settlementDate',
            'terminalId',
            'bankReferenceNumber',
            'invoice',
            'transDateTime',
            'paymentInstrument',
            'transactionType',
            'cardNumber',
            'settlementNetAmount',
            'settlementGrossAmount',
            'referenceNumber',
            'status',
            'externalReferenceNumber',
            'payoutSequenceNumber',
            'internalId',
            'created',
          ],
        }
        const response = yield searchTxnsReq(req, RootStore.session.getTokens, abortController)
        self.transactions = JSON.parse(JSON.stringify(response?.content))
        if (response?.content?.length === 0) {
          throw { message: 'No Record found' }
        }
        return response
      } catch (error) {
        self.transactions = [] as any
        if (error && error?.data?.errorCode !== 'ERR_CANCELED') {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    updateTxnStatus: flow(function* updateTxnStatus(id: string) {
      try {
        const data = self?.transactions?.find((item) => id === item.originalTransactionId)
        const req = {
          entity: {
            type: 12,
            entityId: data?.merchantId,
          },
          updateParameters: {
            transactionId: data?.internalId,
          },
          requestType: riskUpdateMap[data?.status],
          payout: {
            status: riskHoldStatusMap[data?.status] ?? 'HOLD',
          },
        }
        const response = yield updateTxnStatusReq(req, RootStore.session.getTokens)
        if (response?.status === 'SUCCESS') {
          self.transactions = JSON.parse(
            JSON.stringify(self?.transactions?.filter((txn) => txn?.originalTransactionId !== id)),
          )
          RootStore.message.setMessage('Success', MessageTypes.SUCCESS)
        }
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    bulkUpdate: flow(function* (txns: any) {
      const errorData: any = []
      for (let i = 0; i < txns.length; i++) {
        const fileData = txns[i]
        try {
          if (isEmpty(fileData?.transactionId) && isEmpty(fileData?.invoice) && !isEmpty(fileData?.mid)) {
            const traceId = generateRandomString(20)
            const searchMidResponse = yield fetchSearchEntityReq(
              fileData?.mid,
              'Entity ID',
              1,
              5,
              RootStore.session.getTokens,
              'ALL',
              traceId,
            )
            if (searchMidResponse instanceof Object) {
              const response = yield getMidPayoutConfig(fileData?.mid, RootStore.session.getTokens)
              if (
                (fileData?.status?.toLowerCase() === 'release' && response?.settlementOnHold) ||
                (!response?.settlementOnHold && fileData?.status?.toLowerCase() === 'hold') ||
                fileData?.status === ''
              ) {
                const updateResp = yield updateMidPayoutConfigReq(
                  fileData?.mid,
                  { ...response, settlementOnHold: !response?.settlementOnHold },
                  RootStore.session.getTokens,
                  false,
                )
                errorData.push({ status: 'SUCCESS', message: updateResp?.message, code: '' })
              } else {
                errorData.push({ status: 'Failed', message: 'Transaction cant be updated to same state', code: '' })
              }
            } else {
              errorData.push({ status: 'Failed', message: 'Merchant Id not found', code: '' })
            }
          } else if (!isEmpty(fileData?.transactionId) || !isEmpty(fileData?.invoice)) {
            if (isEmpty(fileData?.status) || isEmpty(riskHoldStatusMap[fileData?.status])) {
              errorData.push({ status: 'Failed', message: 'Transaction cant be updated', code: '' })
            } else {
              const req = {
                entity: {
                  type: 12,
                  entityId: fileData?.mid,
                },
                updateParameters: {
                  transactionId: fileData.internalId,
                },
                requestType: riskUpdateMap[fileData?.status],
                payout: {
                  status: riskHoldStatusMap[fileData?.status],
                },
              }
              const response = yield updateTxnStatusReq(req, RootStore.session.getTokens)

              errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
            }
          } else {
            errorData.push({ status: 'Failure', message: 'Mandatory fields missing', code: '' })
          }
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
    fetchGroupByRiskTnxAsync: flow(function* fetchGroupByRiskTnxAsync(
      status: 'RISK_HOLD' | 'PAYOUT_STAGED' | 'LIMIT_HOLD',
      selectedEntityId: any,
      abortController?: any,
    ) {
      console.log('searchTxns abortController', abortController)
      try {
        const pageNumber = RootStore?.pagination?.number === 0 ? 1 : RootStore?.pagination?.number
        const size = RootStore?.pagination?.size < 10 ? 10 : RootStore?.pagination?.size
        const startDate = `${dateFormatter(RootStore?.selectedDateInfo?.fromDate, DateFormatTypes.YYYYMMDD)}00:00:00` //'2023-07-01 00:00:00'
        const endDate = `${dateFormatter(RootStore?.selectedDateInfo?.toDate, DateFormatTypes.YYYYMMDD)}23:59:59`
        const req = {
          inputFilter: {
            startDateTime: startDate,
            endDateTime: endDate,
            status: [status],
            ...(selectedEntityId && { merchantId: selectedEntityId }),
            transactionType: 'TRANSACTION',
          },
          configParams: {
            mediaType: 'JSON',
            page: pageNumber,
            size: size,
          },
        }
        const response = yield fetchGroupByRiskTnxApi(req, abortController)
        self.groupedRiskTnsx = JSON.parse(JSON.stringify(response?.content))
        RootStore.setPagination(response?.page)
        if (response?.content?.length === 0) {
          throw { message: 'No Record found' }
        }
        return response
      } catch (error) {
        self.transactions = [] as any
        if (error && error?.data?.errorCode !== 'ERR_CANCELED') {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    uploadRiskTnxsEvidenceAync: flow(function* uploadRiskTnxsEvidenceAync(file: File, mid: string) {
      try {
        const response = yield uploadRiskTnxsEvidenceApi(file, mid)
        console.log('htag response =', response)
        return response
      } catch (error) {
        RootStore.serviceError.setServiceError('Service Error', '', getErrorMessage(error), '')
        throw error
      }
    }),
    downloadRiskTnxsEvidenceAsync: flow(function* downloadRiskTnxsEvidenceAsync(entityId: string, docId: string) {
      let errorCode = ''
      let resp

      try {
        resp = yield downloadRiskTnxsEvidence(entityId, docId)
        // Below is to check the errorcode in the response as its arraybuffer-
        const errorFileResponse = !(resp.data instanceof ArrayBuffer) && resp?.data //Buffer.from(resp?.data).toString('utf8')
        errorCode = errorFileResponse?.error?.code

        if (window && !errorFileResponse && resp && resp?.data) {
          let contentType = resp.headers['content-type']
          console.log('resp=', resp)
          console.log('contentType=', contentType)
          if (mimeTypes.get(contentType) === undefined) {
            return
          } else {
            const blob = new Blob([resp?.data], { type: contentType })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            document.body.appendChild(a)
            a.href = url
            a.download = `exported-${new Date().getTime()}${mimeTypes.get(contentType)}`
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
          }
        } else {
          throw getErrorMessage(errorFileResponse)
        }
      } catch (e) {
        RootStore.message.setMessage(getErrorMessage(e), MessageTypes.ERROR)
        RootStore.serviceError.setServiceError('Service Error', '', getErrorMessage(e), '')
      }
      return resp
    }),

    processTnxsAsync: flow(function* processTnxsAsync(
      docId: any,
      comments: any,
      status: any,
      entityId: any,
      entityName: any,
    ) {
      try {
        const req = {
          docId,
          comments,
          requestType: riskUpdateMap[status],
          entityId,
          status: riskHoldStatusMap[status],
          entityName,
          transactionList: self.transactions
            .filter((transaction) => transaction.isSelected)
            .map((transaction) => ({
              amount: transaction.settlementGrossAmount,
              transactionType: transaction.transactionType,
              transactionId: transaction.originalTransactionId,
              internalId: transaction.internalId,
            })),
        }

        const response = yield processTransactionApi(req)
        console.log('htag response =', response)
        RootStore.message.setMessage('Record updated sucessfully.', 'POSITIVE')
        return response
      } catch (error) {
        RootStore.serviceError.setServiceError('Service Error', '', getErrorMessage(error), '')
        throw error
      }
    }),
    onTransactionListCheckChanged: flow(function* onTransactionListCheckChanged(data: any, isChecked: boolean) {
      self.transactions.find(
        (transaction) => transaction.originalTransactionId === data.originalTransactionId,
      ).isSelected = isChecked
    }),
    onTransactionListAllCheckChanged: flow(function* onTransactionListCheckChanged(isChecked: boolean) {
      self.transactions.forEach((transaction) => (transaction.isSelected = isChecked)) as any
    }),
    auditListAsync: flow(function* auditListAsync(
      type: 'RISK_HOLD' | 'LIMIT_HOLD',
      selectedEntityId,
      abortController?: any,
    ) {
      console.log('searchTxns abortController', abortController)
      try {
        const pageNumber = RootStore?.pagination?.number === 0 ? 1 : RootStore?.pagination?.number
        const size = RootStore?.pagination?.size < 10 ? 10 : RootStore?.pagination?.size
        const startDate = `${dateFormatter(RootStore?.selectedDateInfo?.fromDate, DateFormatTypes.YYYYMMDD)}00:00:00` //'2023-07-01 00:00:00'
        const endDate = `${dateFormatter(RootStore?.selectedDateInfo?.toDate, DateFormatTypes.YYYYMMDD)}23:59:59`
        const req = {
          inputFilter: {
            fromDate: startDate,
            toDate: endDate,
            type: [type],
            transactionType: ['Purchase'],
            entityId: selectedEntityId,
          },
          configParams: {
            page: pageNumber,
            size: size,
          },
        }
        console.log('req =', req)
        const response = yield fetchGroupByRiskAudit(req, abortController)
        console.log('htag response =', response)
        self.groupedAudit = JSON.parse(JSON.stringify(response?.content))
        RootStore.setPagination({
          size: response?.size,
          totalElements: response?.totalElements,
          totalPages: response?.totalPages,
          number: response?.pageNo,
        })
        if (response?.content?.length === 0) {
          throw { message: 'No Record found' }
        }
        return response
      } catch (error) {
        self.transactions = [] as any
        if (error && error?.data?.errorCode !== 'ERR_CANCELED') {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    auditForAMidAsync: flow(function* auditForAMidAsync(type, mid: string, pageNumber, size, abortController?: any) {
      console.log('searchTxns abortController', status)
      try {
        const startDate = `${dateFormatter(RootStore?.selectedDateInfo?.fromDate, DateFormatTypes.YYYYMMDD)}00:00:00` //'2023-07-01 00:00:00'
        const endDate = `${dateFormatter(RootStore?.selectedDateInfo?.toDate, DateFormatTypes.YYYYMMDD)}23:59:59`
        const req = {
          inputFilter: {
            entityId: mid,
            fromDate: startDate,
            toDate: endDate,
            type: [type],
            transactionType: ['Purchase'],
          },
          configParams: {
            page: pageNumber,
            size: size,
          },
        }
        console.log('req =', req)
        const response = yield auditForAMidApi(req, abortController)
        console.log('htag response =', response)

        self.auditForAMid = JSON.parse(JSON.stringify(response?.content)).map((item) => ({
          ...item,
          comments: item?.comments ?? '',
          actionDownload: item?.docId ? 'Download' : '',
        }))

        if (response?.content?.length === 0) {
          throw { message: 'No Record found' }
        }
        return response
      } catch (error) {
        self.transactions = [] as any
        if (error && error?.data?.errorCode !== 'ERR_CANCELED') {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    exportAudit: flow(function* exportAudit(entityId: string, type: string, getAbortController: any) {
      const startDate = `${dateFormatter(RootStore?.selectedDateInfo?.fromDate, DateFormatTypes.YYYYMMDD)}00:00:00` //'2023-07-01 00:00:00'
      const endDate = `${dateFormatter(RootStore?.selectedDateInfo?.toDate, DateFormatTypes.YYYYMMDD)}23:59:59`
      const req = {
        entityId: entityId,
        fromDate: startDate,
        toDate: endDate,
        type: [type],
        transactionType: ['Purchase'],
      }
      const abortController = getAbortController()
      try {
        const resp = yield exportAuditApi(req, RootStore.session.getTokens, abortController)
        const fileName = resp?.data?.content?.fileName
        if (resp?.data?.status === 'SUCCESS' && fileName) {
          let noOfRetry = 0
          while (noOfRetry < 20) {
            noOfRetry++
            const downloadFileReps = yield (self as any).downloadExportAudit(fileName, abortController)
            if (downloadFileReps?.status === 'PENDING') {
              yield delay(getIncreasingDelay(noOfRetry))
            } else if (downloadFileReps?.status === 'SUCCESS') {
              break
            } else {
              throw new Error(downloadFileReps?.message)
            }
          }
        } else {
          throw new Error('an unexpected error has occured')
        }
      } catch (e) {
        RootStore.message.setMessage(getErrorMessage(e), MessageTypes.ERROR)
        console.log({ e })
      }
    }),
    downloadExportAudit: flow(function* downloadExportAudit(fileName: string, abortController: any) {
      let errorCode = ''
      let resp: any = {}
      console.log('getAbortController downloadExportAudit', abortController)
      try {
        resp = yield downloadExportAuditApi(fileName, RootStore.session.getTokens, abortController)
        if (window && !errorCode && resp && resp?.data && resp?.data instanceof ArrayBuffer) {
          let contentType = resp.headers['content-type']
          if (mimeTypes.get(contentType) === undefined) {
            return { status: 'FAILED' }
          } else {
            const blob = new Blob([resp?.data], { type: contentType })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            document.body.appendChild(a)
            a.href = url
            a.download = `exported-${new Date().getTime()}${mimeTypes.get(contentType)}`
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
            return { status: 'SUCCESS' }
          }
        } else {
          const status = resp?.data?.meta?.status
          if (status === 2) {
            return { status: 'PENDING' }
          } else {
            return { status: 'FAILED', message: resp?.data?.meta?.message }
          }
        }
      } catch (e) {
        console.log({ e })
      }
      return { status: 'FAILED' }
    }),
    setRiskStatus(status: string) {
      self.riskStatus = status
    },
    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield riskControlTxnProcessApproval(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Approved', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    processApprovalsMid: flow(function* processApprovalsMid(data: any) {
      try {
        const response = yield riskControlMerchantProcessApproval(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Approved', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    bulkApproval: flow(function* (approveList: any, type: string) {
      const errorData: any = []
      for (let i = 0; i < approveList.length; i++) {
        const data = approveList[i]
        const userName = data?.username
        if (userName === RootStore.user.name) {
          errorData.push({
            status: 'Failure',
            message: 'Logged in user and Checker can not be same',
            code: '',
          })
        } else {
          try {
            const response = yield bulkEssProcessApprovals(data, type, RootStore.session.getTokens)
            errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
          } catch (e) {
            errorData.push({
              status: 'Failure',
              message: getErrorMessage(e),
              code: (e as any)?.data?.error?.code ?? '',
            })
          }
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
  }))
  .views((self) => ({
    getTransactionWithId(id: string) {
      const txn = self.transactions.find((item: any) => item.id === id)
      const keys = Object.keys(txn)
      return keys.map((key) => {
        return {
          title: key,
          value: txn[key],
        }
      })
    },
    getHasTransactionChecked() {
      return self.transactions.find((item) => item.isSelected)?.status
    },
    getTransactionStatus(id: string) {
      return self.transactions.find((item: any) => item.id === id)?.status
    },
    getTransactions(id: string) {
      return self.transactions.map((item: any, index: number) => ({
        ...item,
        id: item?.originalTransactionId ?? `${index}`,
        settlementGrossAmount: item?.settlementGrossAmount ?? 'NA',
        status: item?.status ?? 'NA',
        mid: item?.merchantId ?? 'NA',
        invoice: item?.invoice ?? 'NA',
        actionHoldRelease: statusActionMap[item?.status] ?? 'Hold/Release',
      }))
    },
    getTransactionList() {
      return self.transactions.map((item: any, index: number) => ({
        ...item,
        settlementNetAmount: '₹ ' + item?.settlementNetAmount,
        transDateTime: dateFormatter(item?.transDateTime, DateFormatTypes.DATE_AM_PM),
        created: dateFormatter(item?.created, DateFormatTypes.DATE_AM_PM),
      }))
    },
    getGroupedAuditList() {
      return self.groupedAudit.map((item: any) => ({
        ...item,
        settlementNetAmount: item?.settlementNetAmount && '₹ ' + item?.settlementNetAmount,
      }))
    },
    getGroupedTnxsList() {
      return self.groupedRiskTnsx.map((item: any) => ({
        ...item,
        settlementNetAmount: item?.settlementNetAmount && '₹ ' + item?.settlementNetAmount,
        created: dateFormatter(item?.created, DateFormatTypes.DATE_ONLY_WITHOUT_TIME),
      }))
    },
  }))

const riskTxnsData = RiskConfig.create({
  transactions: [],
  pagination: {},
})

export { RiskConfig, Transaction, riskTxnsData }
